// apps/web/src/components/painel/home/apoio-kv.tsx — Fase 17.8, Plano 06 (D-05, D-08; UI-SPEC U-04 bloco B, U-17).
// Um número de apoio do hero (data-testid="painel-apoio-{indice}"): chip do perfil, rótulo com período, valor, linha de apoio e
// tendência contra a janela anterior. É um link (<a>) só quando a sessão pode abrir o destino (`podeAbrirRota`); sem destino
// permitido é um <div> com o mesmo layout e sem seta. Zero medido (0), sem medição (— Sem medição) e falha (Indisponível, com
// "Tentar novamente") são apresentações distintas. Só pesos 400/600; a cor vem de --perfil-chip-N (CSS), nunca de matiz solto.
import type { ReactElement } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpRight, Boxes, CalendarCheck, ClipboardCheck, ClipboardList, Factory, FileCheck, Hourglass, Minus, PaintBucket, RotateCcw, ShieldAlert, Truck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ItemApoio } from '@pieltec/contracts';
import type { AtributosBrilho } from '../../../lib/painel/home/brilho';
import { CATALOGO_METRICAS } from '../../../lib/painel/home/catalogo-metricas';
import { formatarNumero, formatarPontosPercentuais } from '../../../lib/painel/home/formato';
import { textoDoMotivoSemMedicao } from '../../../lib/painel/home/motivo-sem-medicao';
import { tomDaTendencia } from '../../../lib/painel/home/polaridade';
import { podeAbrirRota } from '../../../lib/painel/blocos';
import { Button } from '../../ui/button';
import { rotuloDaUnidade } from './heroi-metrica';
import { NumeroAoVivo, type OrigemNumero } from './numero-ao-vivo';
import '../../../styles/painel-home.css';

/** Ícones Lucide dos kv de apoio, por nome do catálogo (o catálogo guarda só o nome). */
const ICONES: Readonly<Record<string, LucideIcon>> = { AlertTriangle, Boxes, CalendarCheck, ClipboardCheck, ClipboardList, Factory, FileCheck, Hourglass, PaintBucket, RotateCcw, ShieldAlert, Truck };

const TEXTO_INDISPONIVEL = 'Não foi possível medir este indicador.';

export interface ApoioKvProps {
  /** Posição 1..3 do kv no catálogo do perfil: estável mesmo se outro kv for omitido (define o chip). */
  readonly indice: number;
  readonly item: ItemApoio;
  readonly permissoes: readonly string[];
  /** Recarrega só esta região (botão exibido quando o kv está indisponível). */
  readonly onTentarNovamente?: () => void;
  /** Origem da mudança do valor: contagem na primeira carga, troca silenciosa ou rolo ao vivo. */
  readonly origem: OrigemNumero;
  /** Marcação do brilho de ~1 s (`data-brilho`) ou do contorno estático (movimento reduzido). */
  readonly brilho?: AtributosBrilho;
}

export function ApoioKv({ indice, item, permissoes, onTentarNovamente, origem, brilho = {} }: ApoioKvProps): ReactElement {
  const entrada = CATALOGO_METRICAS[item.id];
  const insumosDoRotulo = item.metrica.estado === 'MEDIDO' ? { secundarios: item.metrica.secundarios, cobertura: item.metrica.cobertura } : {};
  const rotulo = entrada?.rotuloDe?.(insumosDoRotulo) ?? entrada?.rotulo ?? item.id;
  const Icone = ICONES[entrada?.icone ?? ''] ?? Minus;
  const destino = entrada?.destino;
  const metrica = item.metrica;
  const atributos = { 'data-testid': `painel-apoio-${indice}`, 'data-chip': String(indice), ...brilho } as const;

  const chip = (
    <span className="painel-kv__chip" aria-hidden="true">
      <Icone className="size-4" />
    </span>
  );
  const linhaRotulo = (
    <span className="painel-kv__rotulo">
      {rotulo}
      {entrada?.sufixoPeriodo !== undefined && <span> {entrada.sufixoPeriodo}</span>}
    </span>
  );

  if (metrica.estado === 'INDISPONIVEL') {
    return (
      <div className="painel-kv" {...atributos}>
        {chip}
        <span className="painel-kv__corpo">
          {linhaRotulo}
          <span className="painel-kv__indisponivel">Indisponível</span>
          <span className="painel-kv__apoio">{TEXTO_INDISPONIVEL}</span>
          {onTentarNovamente !== undefined && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start [[data-density=comfortable]_&]:h-11"
              onClick={onTentarNovamente}
              data-testid={`painel-regiao-apoio-${indice}-retry`}
            >
              <RotateCcw aria-hidden="true" />
              <span>Tentar novamente</span>
            </Button>
          )}
        </span>
      </div>
    );
  }

  const podeVerLista = destino !== undefined && podeAbrirRota(destino, permissoes);
  const seta = podeVerLista ? <ArrowUpRight className="painel-kv__seta" aria-hidden="true" /> : null;

  if (metrica.estado === 'SEM_MEDICAO') {
    // Texto da definição do catálogo, senão o motivo da API em pt-BR (D3 da UAT), senão o "Sem medição" de sempre.
    const textoSemMedicao = entrada?.textoSemMedicao ?? textoDoMotivoSemMedicao(metrica.motivo);
    const corpo = (
      <>
        {chip}
        <span className="painel-kv__corpo">
          {linhaRotulo}
          <span className="painel-kv__valor painel-kv__valor--vazio">—</span>
          <span className="painel-kv__apoio">{textoSemMedicao ?? 'Sem medição'}</span>
        </span>
        {seta}
      </>
    );
    return podeVerLista ? (
      <a className="painel-kv painel-kv--link" href={destino} aria-label={`${rotulo}: ${textoSemMedicao ?? 'sem medição.'}`} {...atributos}>
        {corpo}
      </a>
    ) : (
      <div className="painel-kv" {...atributos}>
        {corpo}
      </div>
    );
  }

  const valor = typeof metrica.valor === 'number' ? metrica.valor : Number(metrica.valor);
  const unidade = rotuloDaUnidade(metrica.unidade, valor);
  const casas = metrica.unidade === 'pct' ? 1 : 0;
  const apoio = entrada?.textoApoio?.({ secundarios: metrica.secundarios, cobertura: metrica.cobertura });
  const comparacao = metrica.comparacao;
  const variacao = comparacao?.variacao;
  const tom = variacao === undefined || comparacao === undefined ? 'neutra' : tomDaTendencia(variacao, comparacao.polaridade);
  const diasAnteriores = metrica.periodo.dias ?? 30;
  const textoTendencia = variacao === undefined ? undefined : formatarPontosPercentuais(variacao);
  const IconeTendencia = variacao === undefined || Math.round(variacao * 10) === 0 ? Minus : variacao > 0 ? ArrowUp : ArrowDown;
  const nomeAcessivel = [
    `${rotulo}: ${formatarNumero(valor, casas)} ${unidade}.`,
    apoio !== undefined ? `${apoio}.` : undefined,
    textoTendencia !== undefined ? `${textoTendencia} em relação aos ${diasAnteriores} dias anteriores.` : undefined,
  ]
    .filter((parte): parte is string => parte !== undefined)
    .join(' ');

  const corpo = (
    <>
      {chip}
      <span className="painel-kv__corpo">
        {linhaRotulo}
        <span className="painel-kv__valor">
          <NumeroAoVivo valor={valor} formatar={(v) => formatarNumero(v, casas)} origem={origem} />
          <span className="painel-kv__unidade">{unidade}</span>
        </span>
        {apoio !== undefined && <span className="painel-kv__apoio">{apoio}</span>}
      </span>
      {textoTendencia !== undefined && (
        <span className={`painel-kv__tendencia painel-kv__tendencia--${tom}`} data-testid={`painel-apoio-${indice}-tendencia`} data-tom={tom}>
          <IconeTendencia className="size-3" aria-hidden="true" />
          <span>{textoTendencia}</span>
          <span className="sr-only">{`em relação aos ${diasAnteriores} dias anteriores`}</span>
        </span>
      )}
      {seta}
    </>
  );

  // Alerta (ex.: Omie com ação necessária): o número fica em vermelho por CSS e o atributo garante que a cor não é o único sinal.
  // Aviso (ex.: NCs abertas há mais tempo que o limite): o número vai para --color-warning; `data-aviso` garante que a cor não é o único sinal.
  const atributosDoValor =
    entrada?.alertaSeMaiorQueZero === true && valor > 0
      ? { ...atributos, 'data-alerta': 'true' }
      : entrada?.avisoSeMaiorQueZero === true && valor > 0
        ? { ...atributos, 'data-aviso': 'true' }
        : atributos;

  return podeVerLista ? (
    <a className="painel-kv painel-kv--link" href={destino} aria-label={nomeAcessivel} {...atributosDoValor}>
      {corpo}
    </a>
  ) : (
    <div className="painel-kv" {...atributosDoValor}>
      {corpo}
    </div>
  );
}
